import React, { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Loader2, Check, Repeat, FileText, Camera, Sparkles } from "lucide-react";
import { api, formatMXN } from "@/lib/api";
import { FREQUENCIES } from "@/lib/constants";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const baseForm = {
  type: "to", amount: "", category: "", responsible: "", account_id: "", note: "",
  movement_date: new Date().toISOString().slice(0, 10), is_recurrent: false,
  frequency: "mensual", requires_invoice: false,
};

export default function MovementForm({ initial = null, onSaved, embedded = false }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [form, setForm] = useState(initial ? {
    ...baseForm, ...initial, amount: String(initial.amount ?? ""),
    account_id: initial.account_id || "", movement_date: initial.movement_date || baseForm.movement_date,
  } : baseForm);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [receiptId, setReceiptId] = useState(initial?.receipt_file_id || null);

  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: async () => (await api.get("/accounts")).data });
  const { data: categories = { by: [], to: [] } } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: responsibles = [] } = useQuery({ queryKey: ["responsibles"], queryFn: async () => (await api.get("/responsibles")).data });

  const isBy = form.type === "by";
  const catList = (categories[form.type] || []).map((c) => c.name);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectType = (t) => setForm((f) => ({ ...f, type: t, category: "" }));

  const onPickReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/receipts/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { file_id, extracted } = res.data;
      setReceiptId(file_id);
      setForm((f) => {
        const next = { ...f, type: "to", receipt_file_id: file_id };
        if (extracted?.amount) next.amount = String(extracted.amount);
        if (extracted?.date) next.movement_date = extracted.date;
        if (extracted?.merchant) next.note = extracted.merchant + (f.note ? ` · ${f.note}` : "");
        if (extracted?.category_guess && (categories.to || []).some((c) => c.name === extracted.category_guess)) {
          next.category = extracted.category_guess;
        }
        return next;
      });
      if (extracted?.error === "no_ocr") toast("Ticket guardado (no se pudo leer con IA)");
      else toast.success("Ticket leído con IA ✨");
    } catch {
      toast.error("No se pudo procesar el ticket");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Ingresa un monto válido");
    if (!form.category) return toast.error("Selecciona una categoría");
    setSubmitting(true);
    try {
      const payload = {
        ...form, amount: Number(form.amount),
        requires_invoice: form.type === "to" ? form.requires_invoice : false,
        geolocation: null, receipt_file_id: receiptId,
      };
      if (initial?.id) await api.put(`/movements/${initial.id}`, payload);
      else await api.post("/movements", payload);
      toast.success(initial ? "Movimiento actualizado" : `Movimiento «${isBy ? "By" : "To"}» registrado`);
      qc.invalidateQueries();
      if (onSaved) onSaved();
      if (!initial) { setForm({ ...baseForm, type: form.type }); setReceiptId(null); }
    } catch {
      toast.error("No se pudo guardar el movimiento");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="movement-form">
      <div className="grid grid-cols-2 gap-3">
        <button type="button" data-testid="type-by-button" onClick={() => selectType("by")}
          className={`rounded-2xl border p-4 text-left transition-all ${isBy ? "border-emerald-500/50 bg-emerald-500/10 ring-2 ring-emerald-500/20" : "border-border bg-card hover:border-emerald-500/30"}`}>
          <ArrowUpRight className={`w-6 h-6 mb-2 ${isBy ? "text-emerald-500" : "text-muted-foreground"}`} strokeWidth={2.4} />
          <p className={`font-bold ${isBy ? "text-emerald-500" : ""}`}>By</p>
          <p className="text-xs text-muted-foreground">Origen de los fondos</p>
        </button>
        <button type="button" data-testid="type-to-button" onClick={() => selectType("to")}
          className={`rounded-2xl border p-4 text-left transition-all ${!isBy ? "border-rose-500/50 bg-rose-500/10 ring-2 ring-rose-500/20" : "border-border bg-card hover:border-rose-500/30"}`}>
          <ArrowDownRight className={`w-6 h-6 mb-2 ${!isBy ? "text-rose-500" : "text-muted-foreground"}`} strokeWidth={2.4} />
          <p className={`font-bold ${!isBy ? "text-rose-500" : ""}`}>To</p>
          <p className="text-xs text-muted-foreground">Destino del dinero</p>
        </button>
      </div>

      {/* Receipt scan */}
      <div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" capture="environment" className="hidden" onChange={onPickReceipt} data-testid="receipt-input" />
        <button type="button" data-testid="scan-receipt-button" onClick={() => fileRef.current?.click()} disabled={scanning}
          className="w-full flex items-center gap-3 rounded-xl border border-dashed border-emerald-500/40 bg-emerald-500/5 px-4 py-3 hover:bg-emerald-500/10 transition-colors">
          {scanning ? <Loader2 className="w-5 h-5 animate-spin text-emerald-500" /> : receiptId ? <Check className="w-5 h-5 text-emerald-500" /> : <Camera className="w-5 h-5 text-emerald-500" />}
          <div className="text-left flex-1">
            <p className="text-sm font-medium flex items-center gap-1.5">Capturar ticket <Sparkles className="w-3.5 h-3.5 text-emerald-500" /></p>
            <p className="text-xs text-muted-foreground">{scanning ? "Leyendo con IA…" : receiptId ? "Ticket adjunto — datos autocompletados" : "Toma o sube la foto y la IA llena el monto"}</p>
          </div>
        </button>
      </div>

      <div className="space-y-2">
        <Label>Monto (MXN)</Label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-numeric">$</span>
          <Input data-testid="quick-register-amount-input" type="number" step="0.01" inputMode="decimal" placeholder="0.00"
            value={form.amount} onChange={(e) => set("amount", e.target.value)}
            className="pl-8 h-14 text-2xl font-numeric font-semibold rounded-xl" />
        </div>
        {form.amount > 0 && <p className="text-xs text-muted-foreground font-numeric">{formatMXN(Number(form.amount))}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoría</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="category-select" className="rounded-xl"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>{catList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Responsable</Label>
          <Input data-testid="responsible-input" list="responsibles-list" placeholder="Personal, Pareja…"
            value={form.responsible || ""} onChange={(e) => set("responsible", e.target.value)} className="rounded-xl" />
          <datalist id="responsibles-list">{responsibles.map((r) => <option key={r.id} value={r.name} />)}</datalist>
        </div>
        <div className="space-y-2">
          <Label>Cuenta</Label>
          <Select value={form.account_id} onValueChange={(v) => set("account_id", v)}>
            <SelectTrigger data-testid="account-select" className="rounded-xl"><SelectValue placeholder="¿De dónde sale/entra?" /></SelectTrigger>
            <SelectContent>
              {accounts.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.bank?.color || "#64748b" }} />
                    {a.name}
                  </span>
                </SelectItem>
              ))}
              {accounts.length === 0 && <div className="px-2 py-3 text-xs text-muted-foreground">Crea cuentas en Configuración</div>}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Fecha</Label>
          <Input data-testid="date-input" type="date" value={form.movement_date} onChange={(e) => set("movement_date", e.target.value)} className="rounded-xl" />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Nota (opcional)</Label>
        <Textarea data-testid="note-input" placeholder="Detalle del movimiento…" value={form.note || ""} onChange={(e) => set("note", e.target.value)} className="rounded-xl resize-none" rows={2} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
          <div className="flex items-center gap-3">
            <Repeat className="w-[18px] h-[18px] text-muted-foreground" />
            <div><p className="text-sm font-medium">Recurrente</p><p className="text-xs text-muted-foreground">Aparece en Próximos pagos</p></div>
          </div>
          <Switch data-testid="recurrent-switch" checked={form.is_recurrent} onCheckedChange={(v) => set("is_recurrent", v)} />
        </div>
        {form.is_recurrent && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-2 pl-1">
            <Label>Frecuencia</Label>
            <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
              <SelectTrigger data-testid="frequency-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </motion.div>
        )}
        {form.type === "to" && (
          <label className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 cursor-pointer">
            <Checkbox data-testid="requires-invoice-checkbox" checked={form.requires_invoice} onCheckedChange={(v) => set("requires_invoice", !!v)} />
            <FileText className="w-[18px] h-[18px] text-muted-foreground" />
            <div><p className="text-sm font-medium">Requiere factura (SAT)</p><p className="text-xs text-muted-foreground">Va a Pendientes por facturar</p></div>
          </label>
        )}
      </div>

      <Button data-testid="submit-movement-button" type="submit" disabled={submitting}
        className={`w-full py-3.5 rounded-xl text-base ${isBy ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"} text-white`}>
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : initial ? "Guardar cambios" : `Registrar ${isBy ? "By" : "To"}`}
      </Button>
    </form>
  );
}
